// Global Instructions Rule Applied!
//
// Configuration → Follower Agents (admin only).
//
// The operations surface for the internal follower fleet. Everything here was
// previously curl-only: the PROD rollout ran on a hand-written watch script
// because the app had no view of runs, no queue control and no way to trigger
// a backfill. Four sections, in the order an operator uses them:
//
//   Queue     — depth, due-now pressure, tick state, and the controls that
//               matter when a fleet is mid-flight (pause / re-stagger / cancel)
//   Backfill  — dry-run preview first, then an explicit enqueue. The preview
//               is not decoration: it is the only place the eligible/skipped
//               split is visible before real model spend is committed.
//   Runs      — the fleet run log the per-subject Agent tab cannot give
//   Instances — the follower roster with pause/resume, run-now and provision
//
// Chrome copied from DocusignEnvelopesPanel (FormSection + AntD Table +
// Segmented + standard Tag vocabulary); conventions from InternalAgentsPanel
// (token read straight off the store, load() + Promise.all, local state only,
// no Redux slice). Toasts via react-toastify — AntD static message renders
// nowhere on React 19.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Button,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Segmented,
  Select,
  Table,
  Tag,
  Tooltip
} from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { CompanyShortDescriptionsSection } from './CompanyShortDescriptionsSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  OPERATOR_TRIGGERS,
  cancelQueuedAgentRuns,
  fetchAdminAgentInstances,
  type AdminInstancesQuery,
  type AdminInstancesSortKey,
  fetchAdminAgentRuns,
  fetchAgentQueueSummary,
  postAgentBackfill,
  postAgentDeepen,
  provisionAgentInstances,
  restaggerQueuedAgentRuns,
  retryAgentRun,
  runAgentNow,
  setAgentInstanceStatus,
  setAgentQueueActive
} from '../../internal_agents/services/internalAgentsService.ts'
import {
  AdminAgentRun,
  AgentInstance,
  AgentQueueSummary,
  BackfillManifest,
  DeepenPreview
} from '../../../state_manager/data_models/internal_agents_model.ts'
import { fetchInternalAgentsConfig } from '../services/internalAgentsConfigService.ts'
import { InternalAgentTypeAdminRow } from '../../../state_manager/data_models/internal_agents_config_model.ts'
import { searchCompaniesForPopup } from '../../companies/services/companiesService.ts'
import { searchFundsForPopup } from '../../funds/services/fundsService.ts'
import { searchDealsForPopup } from '../../deals/services/dealsService.ts'
import { searchFundraisingForPopup } from '../../fundraising/services/fundraisingService.ts'
import { ProspectRecordFields } from '../../../state_manager/data_models/prospect_record.ts'
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import { generateHighlightsBench, generateHighlightsHistory } from '../../period_highlights/services/periodHighlightsService.ts'
import type { GenerateBenchPayload, GenerateHistoryPayload } from '../../period_highlights/services/periodHighlightsService.ts'
import type { HighlightBenchManifest, HighlightHistoryManifest } from '../../../state_manager/data_models/period_highlights_model.ts'
import {
  HighlightBenchManifestView, HighlightHistoryManifestView, HighlightHistoryRegenerateControls,
  describeBenchEnqueue, describeHistoryEnqueue, describeHistoryReplaces
} from '../../period_highlights/components/HighlightHistoryManifest.tsx'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

// Runs / Followers table window: one server round-trip per scroll-to-bottom.
const RUNS_PAGE_SIZE = 100
const INSTANCES_PAGE_SIZE = 100
// Load the next window when a table body is this close to its bottom.
const SCROLL_THRESHOLD_PX = 300
// The Runs and Followers tables scroll inside their own body, bounded to
// about one viewport (the PipelinesPanel / IndustriesPanel height), so the
// page itself stays a fixed height however many rows load.
const TABLE_BODY_HEIGHT = 'calc(100vh - 320px)'

/** Footer under a windowed table: "100 of 2,142 runs", or "13 runs" once the
 *  whole set is loaded; empty while nothing has loaded. */
const windowLabel = (loaded: number, total: number, noun: string): string => {
  if (!total) return ''
  const plural = `${noun}${total === 1 ? '' : 's'}`
  return loaded < total
    ? `${loaded.toLocaleString()} of ${total.toLocaleString()} ${plural}`
    : `${total.toLocaleString()} ${plural}`
}

// Standard Tag vocabulary (docs/FRONTEND_DESIGN_PATTERNS.md): blue = pending,
// green = complete, gold = needs attention, red = failure, default = inert.
const RUN_STATUS_TAG: Record<string, string> = {
  queued: 'blue',
  dispatched: 'blue',
  processing: 'blue',
  done: 'green',
  skipped: 'default',
  error: 'red',
  unconfigured: 'gold'
}

const SCOPE_LABEL: Record<string, string> = {
  company: 'Company',
  fund: 'Fund',
  deal: 'Deal',
  prospect: 'Prospect',
  lp_status: 'LP status',
  all: 'Entire fleet'
}

// Why a subject is not a backfill candidate. `instance_<status>` variants fall
// through to the raw reason.
const REASON_LABEL: Record<string, string> = {
  no_instance: 'No follower instance',
  already_queued: 'Already queued',
  already_running: 'Run in flight',
  already_processed: 'Already processed',
  eligible: 'Eligible',
  // Deepen (design §7.4.2) reasons.
  type_disabled: 'Agent type disabled',
  not_window_type: 'Does not read interaction windows',
  not_processed: 'Never processed — use Backfill',
  history_complete: 'Whole history already read',
  floor_not_before_ceiling: 'Date is not before the read history',
  nothing_earlier: 'No earlier interactions'
}

const REASON_TAG: Record<string, string> = {
  no_instance: 'gold',
  already_queued: 'blue',
  already_running: 'blue',
  already_processed: 'default',
  eligible: 'green',
  type_disabled: 'gold',
  not_window_type: 'default',
  not_processed: 'gold',
  history_complete: 'default',
  floor_not_before_ceiling: 'gold',
  nothing_earlier: 'default'
}

// `instance_<status>` covers every non-active instance status, so it is a
// family rather than a fixed key — "instance_paused" becomes "Follower paused".
const reasonLabel = (reason: string): string => {
  if (REASON_LABEL[reason]) return REASON_LABEL[reason]
  if (reason.startsWith('instance_')) {
    return `Follower ${reason.slice('instance_'.length).replace(/_/g, ' ')}`
  }
  return reason
}

type ScopeKind = 'company' | 'fund' | 'deal' | 'prospect' | 'lp_status' | 'all'
// The highlight trackers' history form: one or more subjects (a multi-select
// since 2026-10-29 — the owner regenerates a handful of SPVs after a brief
// change without walking the fleet), or the whole fleet; what it does:
// generate the sets, or add the runners-up to existing ones (2026-10-29,
// model-free).
type HistoryScope = 'subject' | 'all'
type HistoryMode = 'sets' | 'bench'
type HistoryKind = 'fund' | 'fund_group' | 'lp_account'
const HISTORY_KIND: Record<string, HistoryKind> = { funds: 'fund', fund_groups: 'fund_group', accounts: 'lp_account' }
const HISTORY_LABEL: Record<HistoryKind, string> = { fund: 'Fund', fund_group: 'Consolidated fund', lp_account: 'LP' }
const HISTORY_LABEL_PLURAL: Record<HistoryKind, string> = { fund: 'funds', fund_group: 'consolidated funds', lp_account: 'LPs' }
const EMPTY_OPTIONS: PickerOption[] = []
type PickerOption = { value: string; label: string }

const fmtDate = (value: string | null | undefined): string =>
  value ? dayjs(value).format('MM/DD/YYYY') : '—'

const fmtDateTime = (value: string | null | undefined): string =>
  value ? dayjs(value).format('MM/DD/YYYY HH:mm') : '—'

const fmtCost = (value: number | null | undefined): string =>
  value == null ? '—' : `$${Number(value).toFixed(2)}`

export default function FollowerAgentsPanel(): React.ReactElement {
  // Call-time thunk, never a render-time snapshot: the panel stays open for
  // hours, and a token captured at mount outlives the JWT's ~60-minute
  // lifetime. Admin-gated routes answer an expired token with 403, so the
  // 401 re-login path never fires — each request must read the store's
  // current (auto-refreshed) token.
  const token = () => getAccessToken(store.getState().auth)

  // ── shared ────────────────────────────────────────────────────────────────
  const [agentTypes, setAgentTypes] = useState<InternalAgentTypeAdminRow[]>([])
  const [queue, setQueue] = useState<AgentQueueSummary | null>(null)
  const [queueLoading, setQueueLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  // ── deepen (design §7.4.2): the history behind a processed follower's
  // first backfill floor. The modal previews on open and on every floor
  // change (a cheap read), and enqueues only from an eligible preview.
  const [deepenFor, setDeepenFor] = useState<AgentInstance | null>(null)
  const [deepenFloor, setDeepenFloor] = useState<Dayjs | null>(null)
  const [deepenPreview, setDeepenPreview] = useState<DeepenPreview | null>(null)
  const [deepenBusy, setDeepenBusy] = useState<'preview' | 'enqueue' | null>(null)

  // ── backfill ──────────────────────────────────────────────────────────────
  const [bfType, setBfType] = useState<string | null>(null)
  const [bfScope, setBfScope] = useState<ScopeKind>('company')
  const [bfSubject, setBfSubject] = useState<string | null>(null)
  // LP-status scope (Phase 9): a multi-select over the derived statuses, so
  // "every Target LP" is one click and Active + Target is one call.
  const [bfLpStatusIds, setBfLpStatusIds] = useState<string[]>([])
  const [bfOptions, setBfOptions] = useState<PickerOption[]>([])
  const [bfSearching, setBfSearching] = useState(false)
  const [bfBatch, setBfBatch] = useState<number>(10)
  const [bfStagger, setBfStagger] = useState<number>(5)
  const [manifest, setManifest] = useState<BackfillManifest | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [enqueuing, setEnqueuing] = useState(false)
  // ── highlights history (the trackers' backfill, owner decision 2026-09-07:
  // managed here and nowhere else) ────────────────────────────────────────
  const [hScope, setHScope] = useState<HistoryScope>('subject')
  const [hSubjects, setHSubjects] = useState<string[]>([])
  const [hQuarters, setHQuarters] = useState<number>(4)
  const [hForce, setHForce] = useState(false)
  // Under force: leave the versions a person signed off or edited (2.25).
  const [hKeepReviewed, setHKeepReviewed] = useState(false)
  const [hManifest, setHManifest] = useState<HighlightHistoryManifest | null>(null)
  const [hMode, setHMode] = useState<HistoryMode>('sets')
  const [hBench, setHBench] = useState<HighlightBenchManifest | null>(null)

  // ── runs ──────────────────────────────────────────────────────────────────
  // Infinite scroll (the TopicSubscriptionsPage sentinel idiom): the first
  // window replaces, later windows append; the server says whether more exist.
  const [runs, setRuns] = useState<AdminAgentRun[]>([])
  const [runsLoading, setRunsLoading] = useState(true)
  const [runsHasMore, setRunsHasMore] = useState(false)
  const [runsCount, setRunsCount] = useState(0)
  const [runsLoadingMore, setRunsLoadingMore] = useState(false)
  const runsTableRef = useRef<HTMLDivElement | null>(null)
  const [runType, setRunType] = useState<string | null>(null)
  const [runStatus, setRunStatus] = useState<string | null>(null)
  const [runTrigger, setRunTrigger] = useState<string | null>(null)
  const [runRange, setRunRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)

  // ── instances ─────────────────────────────────────────────────────────────
  // Windowed like the runs: the first window replaces, later ones append.
  const [instances, setInstances] = useState<AgentInstance[]>([])
  const [instLoading, setInstLoading] = useState(true)
  const [instHasMore, setInstHasMore] = useState(false)
  const [instCount, setInstCount] = useState(0)
  const [instLoadingMore, setInstLoadingMore] = useState(false)
  const instTableRef = useRef<HTMLDivElement | null>(null)
  const [instType, setInstType] = useState<string | null>(null)
  const [instStatus, setInstStatus] = useState<string | null>(null)
  // What the box shows vs what the server gets: the query trails the typing
  // by a beat so a name is one read, not one per keystroke.
  const [instSubject, setInstSubject] = useState('')
  const [instSubjectQuery, setInstSubjectQuery] = useState('')
  const [instProcessedBefore, setInstProcessedBefore] = useState<Dayjs | null>(null)
  // One sort column at a time; null = the RPC's default order.
  const [instSort, setInstSort] = useState<{ key: AdminInstancesSortKey; direction: 'asc' | 'desc' } | null>(null)

  useEffect(() => {
    const handle = setTimeout(() => setInstSubjectQuery(instSubject.trim()), 300)
    return () => clearTimeout(handle)
  }, [instSubject])

  const typeOptions = useMemo(
    () =>
      agentTypes.map((t) => ({
        value: t.internal_agent_types__agent_type_key,
        label: t.internal_agent_types__name || t.internal_agent_types__agent_type_key
      })),
    [agentTypes]
  )

  const selectedType = useMemo(
    () => agentTypes.find((t) => t.internal_agent_types__agent_type_key === bfType) || null,
    [agentTypes, bfType]
  )

  const lpStatuses = useSelector(
    (state: any) => (state.globalConfigs?.comms_lp_statuses || []) as Array<{ id: string; label: string }>
  )
  const lpStatusOptions = useMemo(
    () => lpStatuses.map((s) => ({ value: s.id, label: s.label })),
    [lpStatuses]
  )

  // Company scope addresses accounts-subject types, Deal scope deals-subject
  // types, Prospect scope prospects-subject types. Fund scope means "this
  // fund's subjects" and applies to BOTH accounts and prospects: for an
  // accounts type it resolves through the type's cfg backfill_fund_selector
  // (the fund's portfolio companies for a follower, the fund's investors for
  // an LP-side type — Phase 9), for a prospects type through the fund's open
  // fundraising processes (Phase 4). LP status scope (Phase 9) is offered to
  // the LP-side accounts types — the ones whose Fund scope resolves to
  // investors — and multi-selects over the derived statuses. The backend
  // rejects any mismatch, so don't offer it.
  const subjectTable = selectedType?.internal_agent_types__subject_table ?? null
  const fundSelector = selectedType?.internal_agent_types__backfill_fund_selector ?? null
  // A highlight tracker never runs the interaction-window pipeline; its
  // backfill is the highlights history (one period_highlights run per
  // instance and completed quarter), so the form switches shape.
  const isTracker = selectedType?.internal_agent_types__scheduled_run_mode === 'period_highlights'
  const historyKind: HistoryKind = HISTORY_KIND[subjectTable ?? ''] ?? 'fund'
  const cfgFunds = useSelector((state: any) => (state.globalConfigs?.funds ?? EMPTY_OPTIONS) as Array<{ id: string; name?: string | null }>)
  const cfgFundGroups = useSelector((state: any) => (state.globalConfigs?.fund_groups ?? EMPTY_OPTIONS) as Array<{ id: string; name?: string | null }>)
  const cfgInvestorParents = useSelector((state: any) => (state.globalConfigs?.investor_parents ?? EMPTY_OPTIONS) as Array<{ id: string; name: string }>)
  const historySubjectOptions = useMemo<PickerOption[]>(() => {
    const rows = historyKind === 'fund' ? cfgFunds : historyKind === 'fund_group' ? cfgFundGroups : cfgInvestorParents
    return rows.map((r) => ({ value: r.id, label: String(r.name ?? r.id) }))
  }, [historyKind, cfgFunds, cfgFundGroups, cfgInvestorParents])
  const scopeOptions = useMemo(() => {
    const opts: Array<{ label: string; value: ScopeKind }> = []
    if (subjectTable === 'accounts') {
      opts.push({ label: fundSelector === 'fund_lps' ? 'LP' : 'Company', value: 'company' })
    }
    if (subjectTable === 'prospects') opts.push({ label: 'Prospect', value: 'prospect' })
    if (subjectTable === 'accounts' || subjectTable === 'prospects') {
      opts.push({ label: fundSelector === 'fund_lps' ? 'Fund (its LPs)' : 'Fund', value: 'fund' })
    }
    if (subjectTable === 'accounts' && fundSelector === 'fund_lps') {
      opts.push({ label: 'LP status', value: 'lp_status' })
    }
    if (subjectTable === 'deals') opts.push({ label: 'Deal', value: 'deal' })
    opts.push({ label: 'Entire fleet', value: 'all' })
    return opts
  }, [subjectTable, fundSelector])

  const loadQueue = useCallback(async () => {
    setQueueLoading(true)
    try {
      setQueue(await fetchAgentQueueSummary(token()))
    } catch (error) {
      console.error('[configuration] agent queue summary failed:', error)
      showErrorToast(error instanceof Error ? error.message : 'Failed to load the queue')
      setQueue(null)
    } finally {
      setQueueLoading(false)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const runsQuery = useCallback(
    (offset: number) => ({
      agent_type_key: runType || undefined,
      status: runStatus || undefined,
      trigger: runTrigger || undefined,
      date_from: runRange?.[0] ? runRange[0]!.startOf('day').toISOString() : undefined,
      date_to: runRange?.[1] ? runRange[1]!.endOf('day').toISOString() : undefined,
      limit: RUNS_PAGE_SIZE,
      offset
    }),
    [runType, runStatus, runTrigger, runRange]
  )

  const loadRuns = useCallback(async () => {
    setRunsLoading(true)
    try {
      const page = await fetchAdminAgentRuns(runsQuery(0), token())
      setRuns(page.data)
      setRunsHasMore(page.has_more)
      setRunsCount(page.count)
    } catch (error) {
      console.error('[configuration] fleet runs load failed:', error)
      showErrorToast(error instanceof Error ? error.message : 'Failed to load runs')
      setRuns([])
      setRunsHasMore(false)
    } finally {
      setRunsLoading(false)
    }
  }, [runsQuery]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadMoreRuns = useCallback(async () => {
    if (runsLoading || runsLoadingMore || !runsHasMore) return
    setRunsLoadingMore(true)
    try {
      const page = await fetchAdminAgentRuns(runsQuery(runs.length), token())
      setRuns((prev) => [...prev, ...page.data])
      setRunsHasMore(page.has_more)
      setRunsCount(page.count)
    } catch (error) {
      console.error('[configuration] fleet runs load-more failed:', error)
      showErrorToast(error instanceof Error ? error.message : 'Failed to load more runs')
      setRunsHasMore(false)
    } finally {
      setRunsLoadingMore(false)
    }
  }, [runsQuery, runs.length, runsLoading, runsLoadingMore, runsHasMore]) // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll on the AntD table body (the IndustriesPanel idiom), not a
  // page-level sentinel: a sentinel after an unbounded table sat between Runs
  // and Followers, so scrolling down to Followers dragged it through the
  // viewport, appended a window of runs, and shoved Followers back out of
  // view on every pass. The table body is the only thing that grows now.
  useEffect(() => {
    const body = runsTableRef.current?.querySelector('.ant-table-body') as HTMLElement | null
    if (!body) return
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = body
      if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX) loadMoreRuns()
    }
    body.addEventListener('scroll', onScroll)
    return () => body.removeEventListener('scroll', onScroll)
  }, [loadMoreRuns])

  // Filters and sort go to the server (the table is a 100-row window); any
  // change here re-creates loadInstances, which restarts at offset 0.
  const instancesQuery = useCallback(
    (offset: number): AdminInstancesQuery => ({
      agent_type_key: instType ?? undefined,
      status: instStatus ?? undefined,
      subject: instSubjectQuery || undefined,
      processed_before: instProcessedBefore ? instProcessedBefore.startOf('day').toISOString() : undefined,
      sort_key: instSort?.key,
      sort_dir: instSort?.direction,
      limit: INSTANCES_PAGE_SIZE,
      offset
    }),
    [instType, instStatus, instSubjectQuery, instProcessedBefore, instSort]
  )

  const loadInstances = useCallback(async () => {
    setInstLoading(true)
    try {
      const page = await fetchAdminAgentInstances(instancesQuery(0), token())
      setInstances(page.data)
      setInstHasMore(page.has_more)
      setInstCount(page.count)
    } catch (error) {
      console.error('[configuration] agent instances load failed:', error)
      showErrorToast(error instanceof Error ? error.message : 'Failed to load instances')
      setInstances([])
      setInstHasMore(false)
    } finally {
      setInstLoading(false)
    }
  }, [instancesQuery]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadMoreInstances = useCallback(async () => {
    if (instLoading || instLoadingMore || !instHasMore) return
    setInstLoadingMore(true)
    try {
      const page = await fetchAdminAgentInstances(instancesQuery(instances.length), token())
      setInstances((prev) => [...prev, ...page.data])
      setInstHasMore(page.has_more)
      setInstCount(page.count)
    } catch (error) {
      console.error('[configuration] agent instances load-more failed:', error)
      showErrorToast(error instanceof Error ? error.message : 'Failed to load more instances')
      setInstHasMore(false)
    } finally {
      setInstLoadingMore(false)
    }
  }, [instancesQuery, instances.length, instLoading, instLoadingMore, instHasMore]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const body = instTableRef.current?.querySelector('.ant-table-body') as HTMLElement | null
    if (!body) return
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = body
      if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX) loadMoreInstances()
    }
    body.addEventListener('scroll', onScroll)
    return () => body.removeEventListener('scroll', onScroll)
  }, [loadMoreInstances])

  // Controlled sorters (the IndustriesPanel idiom): the header arrow shows
  // the server's sort, and clicking it changes the query, not the rows.
  const handleInstTableChange = useCallback((_p: unknown, _f: unknown, sorter: any) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter
    if (s?.order && s.columnKey) {
      setInstSort({ key: String(s.columnKey) as AdminInstancesSortKey, direction: s.order === 'descend' ? 'desc' : 'asc' })
    } else {
      setInstSort(null)
    }
  }, [])

  const instSortOrder = useCallback(
    (key: AdminInstancesSortKey): 'ascend' | 'descend' | null =>
      instSort?.key === key ? (instSort.direction === 'desc' ? 'descend' : 'ascend') : null,
    [instSort]
  )

  useEffect(() => {
    ;(async () => {
      try {
        const config = await fetchInternalAgentsConfig(token())
        // Every type with a subject table. The highlight trackers
        // (scheduled_run_mode period_highlights) never run the
        // interaction-window pipeline — choosing one switches the Backfill
        // form to the highlights history form, the only place that backfill
        // is managed (owner decision 2026-09-07). The interaction backfill
        // still refuses them server-side.
        const types = (config.agentTypes || []).filter(
          (t) => t.internal_agent_types__subject_table !== null
        )
        setAgentTypes(types)
        if (types.length && !bfType) {
          setBfType(types[0].internal_agent_types__agent_type_key)
        }
      } catch (error) {
        console.error('[configuration] agent types load failed:', error)
        showErrorToast(error instanceof Error ? error.message : 'Failed to load agent types')
      }
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadQueue()
  }, [loadQueue])
  useEffect(() => {
    loadRuns()
  }, [loadRuns])
  useEffect(() => {
    loadInstances()
  }, [loadInstances])

  // Changing the type can invalidate the current scope (a deals type has no
  // Company scope), and any scope change invalidates a preview.
  //
  // Guarded on bfType being resolved: before the types load the only option is
  // "Entire fleet", and reconciling against that would silently leave the
  // widest scope selected once a real type arrived.
  useEffect(() => {
    if (!bfType) return
    if (!scopeOptions.some((o) => o.value === bfScope)) {
      setBfScope(scopeOptions[0]?.value ?? 'all')
    }
    setBfSubject(null)
    setBfLpStatusIds([])
    setBfOptions([])
    setManifest(null)
    setHScope('subject')
    setHSubjects([])
    setHManifest(null)
  }, [bfType]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubjectSearch = useCallback(
    async (term: string) => {
      if (!term || term.trim().length < 2) {
        setBfOptions([])
        return
      }
      setBfSearching(true)
      try {
        if (bfScope === 'company') {
          const result = await searchCompaniesForPopup(term, 10, token())
          setBfOptions((result.data ?? []).map((c) => ({ value: c.value, label: c.label })))
        } else if (bfScope === 'fund') {
          const result = await searchFundsForPopup(term, 20, token())
          setBfOptions(
            (result.data ?? []).map((f: any) => ({
              value: String(f.funds__id),
              label: String(f.funds__name ?? f.funds__id)
            }))
          )
        } else if (bfScope === 'deal') {
          const result = await searchDealsForPopup(term, 10, token())
          setBfOptions(
            (result.data ?? []).map((d: any) => ({
              value: String(d[DealFields.deals__id]),
              label: String(d[DealFields.deals__name] ?? d[DealFields.deals__id])
            }))
          )
        } else if (bfScope === 'prospect') {
          const result = await searchFundraisingForPopup(term, 10, token())
          setBfOptions(
            (result.data ?? []).map((p: any) => ({
              value: String(p[ProspectRecordFields.prospects__id]),
              label: [p[ProspectRecordFields.accounts__name], p[ProspectRecordFields.funds__name]]
                .filter(Boolean)
                .join(' — ') || String(p[ProspectRecordFields.prospects__id])
            }))
          )
        }
      } catch {
        setBfOptions([])
      } finally {
        setBfSearching(false)
      }
    },
    [bfScope] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const backfillPayload = (dryRun: boolean) => {
    if (!bfType) return null
    const base: Record<string, unknown> = {
      agent_type_key: bfType,
      batch_size: bfBatch,
      stagger_minutes: bfStagger,
      dry_run: dryRun
    }
    if (bfScope === 'all') base.scope = 'all'
    else if (bfScope === 'company') base.account_id = bfSubject
    else if (bfScope === 'fund') base.fund_id = bfSubject
    else if (bfScope === 'deal') base.deal_id = bfSubject
    else if (bfScope === 'prospect') base.prospect_id = bfSubject
    else if (bfScope === 'lp_status') base.lp_status_ids = bfLpStatusIds
    return base as Parameters<typeof postAgentBackfill>[0]
  }

  const handlePreview = async () => {
    const payload = backfillPayload(true)
    if (!payload) return
    setPreviewing(true)
    try {
      setManifest(await postAgentBackfill(payload, token()))
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Preview failed')
      setManifest(null)
    } finally {
      setPreviewing(false)
    }
  }

  const handleEnqueue = async () => {
    const payload = backfillPayload(false)
    if (!payload) return
    setEnqueuing(true)
    try {
      const result = await postAgentBackfill(payload, token())
      toast.success(
        `Enqueued ${result.enqueued} run${result.enqueued === 1 ? '' : 's'} — the queue ` +
          'dispatches them on the minute tick'
      )
      setManifest(null)
      await Promise.all([loadQueue(), loadRuns()])
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Enqueue failed')
    } finally {
      setEnqueuing(false)
    }
  }

  // ── highlights history ──────────────────────────────────────────────────
  const historyPayload = (dryRun: boolean): GenerateHistoryPayload | null => {
    if (!bfType || (hScope === 'subject' && hSubjects.length === 0)) return null
    const base: GenerateHistoryPayload = {
      scope_types: [historyKind], quarters: hQuarters, dry_run: dryRun, stagger_minutes: bfStagger, force: hForce,
      keep_reviewed: hForce && hKeepReviewed
    }
    if (hScope === 'subject' && hSubjects.length > 0) {
      if (historyKind === 'fund') base.fund_ids = hSubjects
      else if (historyKind === 'fund_group') base.fund_group_ids = hSubjects
      else base.account_ids = hSubjects
    }
    return base
  }

  const handleHistoryPreview = async () => {
    const payload = historyPayload(true)
    if (!payload) return
    setPreviewing(true)
    try {
      setHManifest(await generateHighlightsHistory(payload, token()))
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Preview failed')
      setHManifest(null)
    } finally {
      setPreviewing(false)
    }
  }

  const handleHistoryEnqueue = async () => {
    const payload = historyPayload(false)
    if (!payload) return
    setEnqueuing(true)
    try {
      const result = await generateHighlightsHistory(payload, token())
      setHManifest(result)
      const summary = describeHistoryEnqueue(result)
      if ((result.enqueued_new ?? result.enqueued) === 0) toast.warning(`${summary} — see the note below`)
      else toast.success(`${summary} — the queue dispatches them on the minute tick`)
      await Promise.all([loadQueue(), loadRuns()])
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Generation failed')
    } finally {
      setEnqueuing(false)
    }
  }

  // ── runners-up backfill (2026-10-29): the history form's scope, model-free ──
  const benchPayload = (dryRun: boolean): GenerateBenchPayload | null => {
    const base = historyPayload(dryRun)
    if (!base) return null
    const { force: _force, keep_reviewed: _keep, ...rest } = base
    return rest
  }

  const handleBenchPreview = async () => {
    const payload = benchPayload(true)
    if (!payload) return
    setPreviewing(true)
    try {
      setHBench(await generateHighlightsBench(payload, token()))
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Preview failed')
      setHBench(null)
    } finally {
      setPreviewing(false)
    }
  }

  const handleBenchEnqueue = async () => {
    const payload = benchPayload(false)
    if (!payload) return
    setEnqueuing(true)
    try {
      const result = await generateHighlightsBench(payload, token())
      setHBench(result)
      const summary = describeBenchEnqueue(result)
      if ((result.enqueued_new ?? result.enqueued) === 0) toast.warning(`${summary} — see the note below`)
      else toast.success(`${summary} — the queue dispatches them on the minute tick`)
      await Promise.all([loadQueue(), loadRuns()])
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'The runners-up backfill failed')
    } finally {
      setEnqueuing(false)
    }
  }

  /** Queue operations share one shape: run it, report it, refresh. */
  const queueOp = async (
    key: string,
    action: () => Promise<string>,
    failureLabel: string
  ): Promise<void> => {
    setBusy(key)
    try {
      toast.success(await action())
      await Promise.all([loadQueue(), loadRuns()])
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : failureLabel)
    } finally {
      setBusy(null)
    }
  }

  const tickActive = queue?.cron_jobs?.find((j) => j.jobname === 'internal-agents-tick')?.active
  const cronUnavailable = queue != null && queue.cron_jobs == null

  const handleToggleTick = () =>
    queueOp(
      'tick',
      async () => {
        const result = await setAgentQueueActive(!tickActive, token())
        if (result.warning) toast.warning(result.warning)
        return result.active ? 'Queue resumed' : 'Queue paused — no new runs will be dispatched'
      },
      'Failed to change the queue state'
    )

  const handleRestagger = () =>
    queueOp(
      'restagger',
      async () => {
        // Server default scope: the operator triggers (backfill + manual).
        const result = await restaggerQueuedAgentRuns({ stagger_minutes: 3 }, token())
        const scope = result.triggers?.length ? ` (${result.triggers.join(' + ')})` : ''
        return `Re-staggered ${result.restaggered} queued run${
          result.restaggered === 1 ? '' : 's'
        }${scope} at ${result.stagger_minutes}-minute intervals`
      },
      'Re-stagger failed'
    )

  const handleRetryOne = (runId: string) =>
    queueOp(
      `retry-${runId}`,
      async () => {
        const result = await retryAgentRun(runId, token())
        return result.status === 'exists'
          ? 'The follower already has a queued run — it covers this retry'
          : 'Re-queued as a fresh run — it dispatches on the minute tick'
      },
      'Retry failed'
    )

  const handleCancelStale = () =>
    queueOp(
      'cancel',
      async () => {
        // Both operator triggers: the interaction backfill ('backfill') and
        // the highlight trackers' Generate-history runs ('manual').
        const result = await cancelQueuedAgentRuns({ triggers: OPERATOR_TRIGGERS }, token())
        return `Cancelled ${result.cancelled} queued backfill / Generate-history run${
          result.cancelled === 1 ? '' : 's'
        } (${OPERATOR_TRIGGERS.join(' + ')})`
      },
      'Cancel failed'
    )

  const handleCancelOne = (runId: string) =>
    queueOp(
      `cancel-${runId}`,
      async () => {
        const result = await cancelQueuedAgentRuns({ run_ids: [runId] }, token())
        return result.cancelled ? 'Run cancelled' : 'Run was no longer queued'
      },
      'Cancel failed'
    )

  const handleInstanceStatus = async (instance: AgentInstance) => {
    const next = instance.agent_instances__status === 'active' ? 'paused' : 'active'
    setBusy(instance.agent_instances__id)
    try {
      await setAgentInstanceStatus(instance.agent_instances__id, next, token())
      toast.success(next === 'paused' ? 'Follower paused' : 'Follower resumed')
      await loadInstances()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to update the follower')
    } finally {
      setBusy(null)
    }
  }

  // Run now offers the type's own scheduled mode beside the default window
  // pass when that mode is an operator-runnable review (cadence_review: the
  // LP relationship manager's and prospect tracker's touchpoint / follow-up
  // gate, which had no UI trigger — only the weekly tick or the API). The
  // choice is per row and defaults to the scheduled mode, since that is what
  // an operator pressing Run now on those types is usually after.
  const OPERATOR_RUN_MODES = useMemo(() => new Set(['cadence_review']), [])
  const RUN_MODE_LABELS: Record<string, string> = {
    window: 'Window pass — read recent interactions',
    cadence_review: 'Cadence review — touchpoint / follow-up gate, drafts if due'
  }
  const [runModeFor, setRunModeFor] = useState<Record<string, string>>({})
  const operatorRunMode = (instance: AgentInstance): string | null => {
    const mode = agentTypes.find(
      (t) => t.internal_agent_types__agent_type_key === instance.agent_type_key
    )?.internal_agent_types__scheduled_run_mode
    return mode && OPERATOR_RUN_MODES.has(mode) ? mode : null
  }

  const handleRunNow = async (instance: AgentInstance) => {
    setBusy(instance.agent_instances__id)
    const scheduled = operatorRunMode(instance)
    const runMode = runModeFor[instance.agent_instances__id] ?? scheduled ?? 'window'
    try {
      await runAgentNow(
        {
          agent_type_key: instance.agent_type_key,
          subject_id: instance.agent_instances__subject_id,
          ...(runMode !== 'window' ? { run_mode: runMode } : {})
        },
        token()
      )
      toast.success(`Run complete for ${instance.subject_name ?? 'the subject'}`)
      await Promise.all([loadInstances(), loadRuns(), loadQueue()])
    } catch (error) {
      // A timeout here does NOT mean the run stopped — it is still executing
      // and billing server-side, so steer away from an immediate retry.
      showErrorToast(
        `${error instanceof Error ? error.message : 'Run failed'} — if this was a timeout the ` +
          'run may still be going; check Runs before re-running.'
      )
      await loadRuns()
    } finally {
      setBusy(null)
    }
  }

  const deepenFloorIso = (floor: Dayjs | null): string | null =>
    floor ? floor.startOf('day').toISOString() : null

  useEffect(() => {
    if (!deepenFor) return
    let cancelled = false
    setDeepenBusy('preview')
    postAgentDeepen(
      {
        instance_id: deepenFor.agent_instances__id,
        floor: deepenFloorIso(deepenFloor),
        dry_run: true
      },
      token()
    )
      .then((preview) => {
        if (!cancelled) setDeepenPreview(preview)
      })
      .catch((error) => {
        if (!cancelled) showErrorToast(error instanceof Error ? error.message : 'Preview failed')
      })
      .finally(() => {
        if (!cancelled) setDeepenBusy(null)
      })
    return () => {
      cancelled = true
    }
  }, [deepenFor, deepenFloor]) // eslint-disable-line react-hooks/exhaustive-deps

  const openDeepen = (instance: AgentInstance) => {
    setDeepenPreview(null)
    setDeepenFloor(null)
    setDeepenFor(instance)
  }

  const handleDeepen = async () => {
    if (!deepenFor) return
    setDeepenBusy('enqueue')
    try {
      const result = await postAgentDeepen(
        {
          instance_id: deepenFor.agent_instances__id,
          floor: deepenFloorIso(deepenFloor),
          dry_run: false
        },
        token()
      )
      if (result.enqueued > 0) {
        toast.success(
          `Deepen queued for ${deepenFor.subject_name ?? 'the follower'}: ` +
            `${result.interactions_in_range ?? 0} interaction${result.interactions_in_range === 1 ? '' : 's'} ` +
            `in about ${result.windows_estimate ?? 0} run${result.windows_estimate === 1 ? '' : 's'}`
        )
        setDeepenFor(null)
        await Promise.all([loadInstances(), loadRuns(), loadQueue()])
      } else {
        // The SQL re-derived eligibility at enqueue time and refused — show
        // the fresh reason rather than the stale preview's.
        setDeepenPreview(result)
        showErrorToast(`Not queued: ${reasonLabel(result.reason)}`)
      }
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Deepen failed')
    } finally {
      setDeepenBusy(null)
    }
  }

  // Provisioning is fleet-scale and arms the inbound-mail trigger, so it acts
  // ONLY on the type selected in this section — never on the Backfill form's
  // type, which the operator is not looking at when they press this.
  const handleProvision = () =>
    queueOp(
      'provision',
      async () => {
        if (!instType) throw new Error('Select an agent type to provision')
        const result = (await provisionAgentInstances(instType, token())) as {
          created?: number
          matching?: number
        }
        await loadInstances()
        return `Provisioned ${result?.created ?? 0} new instance${
          result?.created === 1 ? '' : 's'
        } of ${result?.matching ?? 0} matching subjects`
      },
      'Provisioning failed'
    )

  // ── columns ───────────────────────────────────────────────────────────────

  const candidateColumns = useMemo(
    () => [
      { title: 'Subject', dataIndex: 'subject_name', ellipsis: true },
      {
        title: 'Reason',
        dataIndex: 'reason',
        width: 170,
        render: (value: string) => (
          <Tag color={REASON_TAG[value] ?? 'default'}>{reasonLabel(value)}</Tag>
        )
      },
      {
        title: 'Last processed',
        dataIndex: 'last_processed_interaction_at',
        width: 130,
        render: (value: string | null) => fmtDate(value)
      }
    ],
    []
  )

  const runColumns = useMemo(
    () => [
      {
        title: 'Created',
        dataIndex: 'agent_runs__date_created',
        width: 140,
        render: (value: string) => fmtDateTime(value)
      },
      {
        title: 'Subject',
        dataIndex: 'subject_name',
        ellipsis: true,
        render: (value: string | null) => value || <span className='text-gray-400'>—</span>
      },
      {
        title: 'Agent',
        dataIndex: 'agent_type_key',
        // Wide enough for the longest type name ("Portfolio Company Follower")
        // on one line; Subject and Summary share what is left.
        width: 200,
        render: (value: string) =>
          typeOptions.find((t) => t.value === value)?.label ?? value
      },
      { title: 'Trigger', dataIndex: 'agent_runs__trigger', width: 100 },
      {
        title: 'Status',
        dataIndex: 'agent_runs__status',
        width: 110,
        render: (value: string, record: AdminAgentRun) => {
          const verdict = record.agent_runs__error_class
            ? `${record.agent_runs__error_class} (${record.agent_runs__error_source ?? '?'})`
            : null
          const chain = record.agent_runs__retry_count > 0 ? `retry #${record.agent_runs__retry_count}` : null
          const tip = [record.agent_runs__error, verdict, chain].filter(Boolean).join(' — ')
          return (
            <Tooltip title={tip || undefined}>
              <span className='inline-flex items-center gap-1'>
                <Tag color={RUN_STATUS_TAG[value] ?? 'default'}>{value}</Tag>
                {chain && <span className='text-xs text-gray-400'>#{record.agent_runs__retry_count}</span>}
              </span>
            </Tooltip>
          )
        }
      },
      {
        title: 'Window',
        key: 'window',
        width: 130,
        render: (_: unknown, record: AdminAgentRun) =>
          record.agent_runs__window_from
            ? `${fmtDate(record.agent_runs__window_from)} → ${fmtDate(record.agent_runs__window_to)}`
            : '—'
      },
      {
        title: 'Cost',
        dataIndex: 'agent_runs__total_cost_usd',
        width: 80,
        align: 'right' as const,
        render: (value: number) => fmtCost(value)
      },
      {
        title: 'Summary',
        key: 'summary',
        ellipsis: true,
        render: (_: unknown, record: AdminAgentRun) => (
          <Tooltip title={record.agent_runs__summary || record.agent_runs__error || undefined}>
            <span className={record.agent_runs__error ? 'text-red-500' : undefined}>
              {record.agent_runs__summary || record.agent_runs__error || '—'}
            </span>
          </Tooltip>
        )
      },
      {
        title: '',
        key: 'actions',
        width: 90,
        render: (_: unknown, record: AdminAgentRun) =>
          record.agent_runs__status === 'queued' ? (
            <Popconfirm
              title='Cancel this queued run?'
              description='It has not started, so nothing has been spent.'
              okText='Cancel run'
              onConfirm={() => handleCancelOne(record.agent_runs__id)}
            >
              <Button size='small' danger ghost loading={busy === `cancel-${record.agent_runs__id}`}>
                Cancel
              </Button>
            </Popconfirm>
          ) : (record.agent_runs__status === 'error' || record.agent_runs__status === 'unconfigured') &&
            record.agent_runs__retried_as ? (
            <Tooltip title='Already re-queued (automatically or by an admin) — the fresh run is in this list.'>
              <span className='text-xs text-gray-400'>Retried</span>
            </Tooltip>
          ) : record.agent_runs__status === 'error' || record.agent_runs__status === 'unconfigured' ? (
            <Popconfirm
              title='Retry this run?'
              description='Queues a fresh run for the same follower, mode and period. This run keeps its record; the new one is cross-linked to it.'
              okText='Retry'
              onConfirm={() => handleRetryOne(record.agent_runs__id)}
            >
              <Button size='small' loading={busy === `retry-${record.agent_runs__id}`}>
                Retry
              </Button>
            </Popconfirm>
          ) : null
      }
    ],
    [typeOptions, busy] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const instanceColumns = useMemo(
    () => [
      // Sortable columns carry key = dataIndex = the RPC's sort key; the
      // three counters below are not sortable (the RPC does not offer it).
      {
        title: 'Subject',
        dataIndex: 'subject_name',
        key: 'subject_name',
        ellipsis: true,
        sorter: true,
        sortOrder: instSortOrder('subject_name'),
        render: (value: string | null) => value || <span className='text-gray-400'>—</span>
      },
      {
        title: 'Agent',
        dataIndex: 'agent_type_key',
        key: 'agent_type_key',
        // Wide enough for the longest type name on one line; Subject, the
        // flex column, gives up the difference and ellipsises.
        width: 200,
        sorter: true,
        sortOrder: instSortOrder('agent_type_key'),
        render: (value: string) => typeOptions.find((t) => t.value === value)?.label ?? value
      },
      {
        title: 'Status',
        dataIndex: 'agent_instances__status',
        key: 'agent_instances__status',
        width: 110,
        sorter: true,
        sortOrder: instSortOrder('agent_instances__status'),
        render: (value: string) => (
          <Tag color={value === 'active' ? 'green' : 'default'}>{value}</Tag>
        )
      },
      {
        title: 'Processed to',
        dataIndex: 'agent_instances__last_processed_interaction_at',
        key: 'agent_instances__last_processed_interaction_at',
        width: 135,
        sorter: true,
        sortOrder: instSortOrder('agent_instances__last_processed_interaction_at'),
        render: (value: string | null) =>
          value ? fmtDate(value) : <Tag color='blue'>Never</Tag>
      },
      {
        title: 'Unfollows',
        dataIndex: 'agent_instances__follow_until',
        key: 'agent_instances__follow_until',
        width: 125,
        sorter: true,
        sortOrder: instSortOrder('agent_instances__follow_until'),
        render: (value: string | null) =>
          value ? <span style={{ color: '#d46b08' }}>{fmtDate(value)}</span> : '—'
      },
      { title: 'Signals', dataIndex: 'prog__open_signals', width: 80, align: 'right' as const },
      { title: 'Memories', dataIndex: 'prog__active_memories', width: 90, align: 'right' as const },
      { title: 'Runs', dataIndex: 'prog__runs', width: 70, align: 'right' as const },
      {
        title: '',
        key: 'actions',
        width: 250,
        render: (_: unknown, record: AgentInstance) => (
          <div className='flex gap-1.5'>
            <Button
              size='small'
              loading={busy === record.agent_instances__id}
              onClick={() => handleInstanceStatus(record)}
            >
              {record.agent_instances__status === 'active' ? 'Pause' : 'Resume'}
            </Button>
            <Popconfirm
              title='Run this follower now?'
              description={
                <div className='max-w-md'>
                  {operatorRunMode(record) ? (
                    <Radio.Group
                      className='flex flex-col gap-1 mb-2'
                      value={runModeFor[record.agent_instances__id] ?? operatorRunMode(record)}
                      onChange={(e) =>
                        setRunModeFor((prev) => ({ ...prev, [record.agent_instances__id]: e.target.value }))
                      }
                    >
                      {[operatorRunMode(record) as string, 'window'].map((mode) => (
                        <Radio key={mode} value={mode}>
                          {RUN_MODE_LABELS[mode] ?? mode}
                        </Radio>
                      ))}
                    </Radio.Group>
                  ) : null}
                  Runs immediately and bills for any model call. A cadence review costs nothing
                  when nothing is due. This call is synchronous: a long run can report a timeout
                  while it keeps going server-side, so check the Runs list before trying again
                  rather than re-running.
                </div>
              }
              okText='Run now'
              onConfirm={() => handleRunNow(record)}
            >
              <Button size='small' loading={busy === record.agent_instances__id}>
                Run now
              </Button>
            </Popconfirm>
            {record.agent_instances__last_processed_interaction_at ? (
              <Tooltip title='Read the history behind this follower’s first backfill floor (the earliest interaction it has read), down to a date you choose, without moving its watermark. Previews first.'>
                <Button size='small' onClick={() => openDeepen(record)}>
                  Deepen
                </Button>
              </Tooltip>
            ) : null}
          </div>
        )
      }
    ],
    [instSortOrder, typeOptions, busy, agentTypes, runModeFor] // eslint-disable-line react-hooks/exhaustive-deps
  )

  // ── render ────────────────────────────────────────────────────────────────

  // Same Stat treatment as the per-subject Agent tab's stats strip
  // (internal_agents/components/AgentTab.tsx) — this is the fleet-level
  // reading of the same numbers, so it should not look like a new thing.
  const tile = (label: string, value: React.ReactNode, hint?: string) => (
    <Tooltip title={hint}>
      <div>
        <span className='block text-[10.5px] tracking-wider uppercase text-gray-400'>{label}</span>
        <span className='text-base font-semibold tabular-nums'>{value}</span>
      </div>
    </Tooltip>
  )

  const scopeReady =
    bfScope === 'all' || (bfScope === 'lp_status' ? bfLpStatusIds.length > 0 : !!bfSubject)

  return (
    <div className={`${CONFIG_PANEL_SCROLL_CLASS} space-y-4`} data-component='FollowerAgentsPanel'>
      {/* ── Queue ──────────────────────────────────────────────────────── */}
      <FormSection
        title='Queue'
        titleColor='blue'
        loading={queueLoading}
        className='shadow-sm border border-gray-200'
        headerActions={
          <div className='flex items-center gap-2 flex-wrap justify-end'>
            {tickActive != null && (
              <Tag color={tickActive ? 'green' : 'red'}>
                {tickActive ? 'Tick active' : 'Tick paused'}
              </Tag>
            )}
            <Button size='small' icon={<ReloadOutlined />} onClick={loadQueue} />
          </div>
        }
      >
        <p className='text-gray-500 text-xs mt-0 mb-2'>
          Pausing the tick is the real fleet stop — the Internal Agents “enabled” flag only guards
          the operator entry points, so a queue already in flight keeps draining. Terminal counts
          are for today (UTC); queued, dispatched and processing are live. Spend is in
          Configuration → Internal Agents.
        </p>
        {queue && (
          <div className='flex gap-6 flex-wrap items-center bg-white border border-gray-100 rounded-lg px-4 py-2.5'>
            {tile('Queued', queue.by_status.queued)}
            {tile('Due now', queue.queued_due_now, 'Queued runs the next tick can claim')}
            {tile('Dispatched', queue.by_status.dispatched)}
            {tile('Processing', queue.by_status.processing)}
            {tile('Done today', queue.by_status.done)}
            {tile('Errors today', queue.by_status.error)}
            {tile('Next due', queue.next_due_at ? dayjs(queue.next_due_at).format('HH:mm') : '—')}
            {tile(
              'Oldest queued',
              queue.oldest_queued_at ? fmtDate(queue.oldest_queued_at) : '—'
            )}
          </div>
        )}
        {/* Queue operations live in the section's own action row rather than
            headerActions: four controls plus a status Tag overflow the header
            slot, which does not shrink. */}
        <div className='flex items-center gap-2 flex-wrap mt-2'>
          {/* tickActive is undefined until the summary lands — don't label the
              button from an unknown state, or a loading panel reads as
              "paused" while the fleet is running. */}
          <Popconfirm
            title={tickActive ? 'Pause the agent queue?' : 'Resume the agent queue?'}
            description={
              tickActive
                ? 'Stops dispatching new runs. Runs already in flight finish normally.'
                : 'Queued runs become due immediately. If a backlog has built up, re-stagger it first.'
            }
            okText={tickActive ? 'Pause' : 'Resume'}
            onConfirm={handleToggleTick}
            disabled={cronUnavailable || tickActive == null}
          >
            <Button
              size='small'
              loading={busy === 'tick'}
              disabled={cronUnavailable || tickActive == null}
            >
              {tickActive == null ? 'Tick' : tickActive ? 'Pause tick' : 'Resume tick'}
            </Button>
          </Popconfirm>
          <Popconfirm
            title='Re-stagger queued runs?'
            description='Re-spaces every queued backfill and Generate-history run (triggers backfill + manual) at 3-minute intervals so the queue drains at the rate the backend can process. Live interaction- and schedule-triggered runs keep their slot.'
            okText='Re-stagger'
            onConfirm={handleRestagger}
          >
            <Button size='small' loading={busy === 'restagger'}>
              Re-stagger queued
            </Button>
          </Popconfirm>
          <Popconfirm
            title='Cancel all queued backfill runs?'
            description='Drops every queued backfill and Generate-history run (triggers backfill + manual). Only queued rows are dropped — nothing in flight is touched, and nothing has been spent on them. Live interaction- and schedule-triggered runs stay.'
            okText='Cancel them'
            onConfirm={handleCancelStale}
          >
            <Button size='small' danger ghost loading={busy === 'cancel'}>
              Cancel queued backfills
            </Button>
          </Popconfirm>
        </div>
        {cronUnavailable && (
          <p className='text-gray-500 text-xs mt-2 mb-0'>
            pg_cron is not installed on this database, so the tick cannot be paused from here.
          </p>
        )}
      </FormSection>

      {/* ── Backfill ───────────────────────────────────────────────────── */}
      <FormSection
        title='Backfill'
        titleColor='green'
        className='shadow-sm border border-gray-200'
      >
        {isTracker ? (
        <>
        <p className='text-gray-500 text-xs mt-0 mb-2'>
          A highlight tracker does not process interaction windows: its history is one period
          highlights run per instance and completed quarter. Preview first: the dry run counts the
          sets to generate and the company atoms still missing per period (the cost driver — atoms
          are shared across funds, consolidated funds and LPs, and a company with a live set freezes
          into its atom at no cost). Each instance’s quarters queue one after another; instances are
          staggered. The Period Highlights page generates one scope and period at a time; past
          quarters are managed here.
        </p>
        <div className='flex flex-wrap items-end gap-3'>
          <label className='flex flex-col gap-1'>
            <span className='text-xs text-gray-500'>Agent type</span>
            <Select
              size='small'
              className='w-56'
              value={bfType}
              onChange={(value) => setBfType(value)}
              options={typeOptions}
              placeholder='Select an agent'
            />
          </label>
          <label className='flex flex-col gap-1'>
            <Tooltip title='Generate sets: one period highlights run per instance and quarter (model spend). Add runners-up: a model-free pass that appends the next candidates, as excluded runners-up, to the quarters that already have a current approved set.'>
              <span className='text-xs text-gray-500'>What</span>
            </Tooltip>
            <Segmented
              size='small'
              value={hMode}
              onChange={(value) => { setHMode(value as HistoryMode); setHManifest(null); setHBench(null) }}
              options={[{ label: 'Generate sets', value: 'sets' }, { label: 'Add runners-up', value: 'bench' }]}
            />
          </label>
          <label className='flex flex-col gap-1'>
            <span className='text-xs text-gray-500'>Scope</span>
            <Segmented
              size='small'
              value={hScope}
              onChange={(value) => { setHScope(value as HistoryScope); setHSubjects([]); setHManifest(null); setHBench(null) }}
              options={[{ label: HISTORY_LABEL[historyKind], value: 'subject' }, { label: 'Entire fleet', value: 'all' }]}
            />
          </label>
          {hScope === 'subject' && (
            <label className='flex flex-col gap-1'>
              <Tooltip title={`One or more ${HISTORY_LABEL_PLURAL[historyKind]}: the preview and the runs cover every one selected.`}>
                <span className='text-xs text-gray-500'>{HISTORY_LABEL[historyKind]}</span>
              </Tooltip>
              <Select
                mode='multiple'
                showSearch
                allowClear
                size='small'
                className='w-96'
                maxTagCount='responsive'
                optionFilterProp='label'
                placeholder={`Select one or more ${HISTORY_LABEL_PLURAL[historyKind]}`}
                options={historySubjectOptions}
                value={hSubjects}
                onChange={(value) => { setHSubjects((value as string[] | undefined) ?? []); setHManifest(null); setHBench(null) }}
              />
            </label>
          )}
          <label className='flex flex-col gap-1'>
            <Tooltip title='The last N completed quarters (quarter end plus the type’s generation lag), oldest first.'>
              <span className='text-xs text-gray-500'>Completed quarters</span>
            </Tooltip>
            <InputNumber size='small' min={1} max={12} value={hQuarters} onChange={(value) => { setHQuarters(Number(value) || 1); setHManifest(null) }} />
          </label>
          <label className='flex flex-col gap-1'>
            <Tooltip title='Minutes between consecutive instances. An instance’s own quarters queue one after another.'>
              <span className='text-xs text-gray-500'>Stagger (min)</span>
            </Tooltip>
            <InputNumber size='small' min={0} max={120} value={bfStagger} onChange={(value) => { setBfStagger(Number(value) ?? 0); setHManifest(null) }} />
          </label>
          {hMode === 'sets' && (
            <HighlightHistoryRegenerateControls
              force={hForce}
              keepReviewed={hKeepReviewed}
              onForceChange={(value) => { setHForce(value); setHManifest(null) }}
              onKeepReviewedChange={(value) => { setHKeepReviewed(value); setHManifest(null) }}
            />
          )}
          <Button
            type='primary'
            size='small'
            loading={previewing}
            disabled={!bfType || (hScope === 'subject' && hSubjects.length === 0)}
            onClick={hMode === 'bench' ? handleBenchPreview : handleHistoryPreview}
          >
            Preview (dry run)
          </Button>
        </div>

        {hMode === 'bench' && hBench && (
          <div className='mt-3 border-t border-gray-200 pt-3'>
            <div className='flex gap-6 flex-wrap items-center bg-white border border-gray-100 rounded-lg px-4 py-2.5 mb-2'>
              {tile('Instances', hBench.scopes.reduce((n, sc) => n + sc.instances, 0))}
              {tile('Sets to update', hBench.sets_to_update, 'Quarters with a current approved set: each gets the next candidates appended as excluded runners-up')}
              {tile('Runners-up per set', hBench.bench_items, 'cfg period_highlights.bench_items')}
              <div className='flex-1' />
              {hBench.dry_run && (
                <Popconfirm
                  title={`Add runners-up to ${hBench.sets_to_update} set${hBench.sets_to_update === 1 ? '' : 's'}?`}
                  description={<div className='max-w-sm'>No model spend. Each pass appends up to {hBench.bench_items} excluded runners-up after the selected highlights; runners-up a reviewer already selected stay. Runs are staggered and execute on the queue tick.</div>}
                  okText='Add runners-up'
                  onConfirm={handleBenchEnqueue}
                  disabled={hBench.sets_to_update === 0 || hBench.bench_items === 0}
                >
                  <Button type='primary' loading={enqueuing} disabled={hBench.sets_to_update === 0 || hBench.bench_items === 0}>
                    Add runners-up to {hBench.sets_to_update} set{hBench.sets_to_update === 1 ? '' : 's'}
                  </Button>
                </Popconfirm>
              )}
            </div>
            <HighlightBenchManifestView manifest={hBench} />
          </div>
        )}

        {hMode === 'sets' && hManifest && (
          <div className='mt-3 border-t border-gray-200 pt-3'>
            <div className='flex gap-6 flex-wrap items-center bg-white border border-gray-100 rounded-lg px-4 py-2.5 mb-2'>
              {tile('Instances', hManifest.scopes.reduce((n, sc) => n + sc.instances, 0))}
              {tile('Sets to generate', hManifest.sets_to_generate)}
              {tile('Atoms to generate', hManifest.atoms_missing, 'Company atoms the runs will generate across the periods — the cost driver. Approved atoms are reused; without Regenerate, live sets freeze into atoms at no cost.')}
              {tile('Estimate', `$${hManifest.estimate.total_usd.toFixed(2)}`, 'Guidance only; the daily cap defers runs rather than failing them')}
              <div className='flex-1' />
              {hManifest.dry_run && (
                <Popconfirm
                  title={`Generate ${hManifest.sets_to_generate} set${hManifest.sets_to_generate === 1 ? '' : 's'}?`}
                  description={
                    <div className='max-w-sm'>
                      This commits real model spend. Runs are staggered and execute on the queue tick.
                      {describeHistoryReplaces(hManifest) && <div className='mt-1 text-amber-700'>{describeHistoryReplaces(hManifest)}</div>}
                    </div>
                  }
                  okText='Generate'
                  onConfirm={handleHistoryEnqueue}
                  disabled={hManifest.sets_to_generate === 0}
                >
                  <Button type='primary' loading={enqueuing} disabled={hManifest.sets_to_generate === 0}>
                    Generate {hManifest.sets_to_generate} set{hManifest.sets_to_generate === 1 ? '' : 's'}
                  </Button>
                </Popconfirm>
              )}
            </div>
            <HighlightHistoryManifestView manifest={hManifest} />
          </div>
        )}
        </>
        ) : (
        <>
        <p className='text-gray-500 text-xs mt-0 mb-2'>
          Queues historical processing for subjects that have never been processed. Preview first:
          the dry run shows exactly who is eligible and why the rest are skipped, before any model
          spend is committed. Backfill only enqueues — the pg_cron tick executes, cost caps defer
          to the next UTC day, and re-issuing the same scope safely picks up where the last batch
          stopped.
        </p>
        <div className='flex flex-wrap items-end gap-3'>
          <label className='flex flex-col gap-1'>
            <span className='text-xs text-gray-500'>Agent type</span>
            <Select
              size='small'
              className='w-56'
              value={bfType}
              onChange={(value) => setBfType(value)}
              options={typeOptions}
              placeholder='Select an agent'
            />
          </label>
          <label className='flex flex-col gap-1'>
            <span className='text-xs text-gray-500'>Scope</span>
            <Segmented
              size='small'
              value={bfScope}
              onChange={(value) => {
                setBfScope(value as ScopeKind)
                setBfSubject(null)
                setBfLpStatusIds([])
                setBfOptions([])
                setManifest(null)
              }}
              options={scopeOptions}
            />
          </label>
          {bfScope === 'lp_status' && (
            <label className='flex flex-col gap-1'>
              <span className='text-xs text-gray-500'>LP statuses</span>
              <Select
                mode='multiple'
                size='small'
                className='w-72'
                placeholder='Select one or more statuses'
                options={lpStatusOptions}
                value={bfLpStatusIds}
                onChange={(value) => {
                  setBfLpStatusIds(value as string[])
                  setManifest(null)
                }}
              />
            </label>
          )}
          {bfScope !== 'all' && bfScope !== 'lp_status' && (
            <label className='flex flex-col gap-1'>
              <span className='text-xs text-gray-500'>{SCOPE_LABEL[bfScope]}</span>
              <Select
                showSearch
                size='small'
                className='w-72'
                placeholder={`Search ${SCOPE_LABEL[bfScope].toLowerCase()}s…`}
                filterOption={false}
                onSearch={handleSubjectSearch}
                loading={bfSearching}
                options={bfOptions}
                value={bfSubject}
                onChange={(value) => {
                  setBfSubject(value)
                  setManifest(null)
                }}
                notFoundContent={bfSearching ? 'Searching…' : 'Type at least 2 characters'}
              />
            </label>
          )}
          <label className='flex flex-col gap-1'>
            <Tooltip title='How many subjects this call enqueues (max 50). Re-issue the call to take the next batch.'>
              <span className='text-xs text-gray-500'>Batch size</span>
            </Tooltip>
            <InputNumber
              size='small'
              min={1}
              max={50}
              value={bfBatch}
              onChange={(value) => {
                setBfBatch(Number(value) || 1)
                setManifest(null)
              }}
            />
          </label>
          <label className='flex flex-col gap-1'>
            <Tooltip title='Minutes between queued runs. Spacing keeps the queue from filling faster than the backend drains it.'>
              <span className='text-xs text-gray-500'>Stagger (min)</span>
            </Tooltip>
            <InputNumber
              size='small'
              min={0}
              max={120}
              value={bfStagger}
              onChange={(value) => {
                setBfStagger(Number(value) ?? 0)
                setManifest(null)
              }}
            />
          </label>
          <Button
            type='primary'
            size='small'
            loading={previewing}
            disabled={!bfType || !scopeReady}
            onClick={handlePreview}
          >
            Preview (dry run)
          </Button>
        </div>

        {manifest && (
          <div className='mt-3 border-t border-gray-200 pt-3'>
            <div className='flex gap-6 flex-wrap items-center bg-white border border-gray-100 rounded-lg px-4 py-2.5 mb-2'>
              {tile('Candidates', manifest.candidates_total)}
              {tile('Eligible', manifest.eligible_total)}
              {tile('This batch', manifest.selected_total)}
              {tile('Remaining', manifest.remaining_eligible)}
              <div className='flex-1' />
              <Popconfirm
                title={`Enqueue ${manifest.selected_total} run${
                  manifest.selected_total === 1 ? '' : 's'
                }?`}
                description='This commits real model spend. Runs are staggered and execute on the queue tick.'
                okText='Enqueue'
                onConfirm={handleEnqueue}
                disabled={manifest.selected_total === 0}
              >
                <Button
                  type='primary'
                  loading={enqueuing}
                  disabled={manifest.selected_total === 0}
                >
                  Enqueue {manifest.selected_total} run
                  {manifest.selected_total === 1 ? '' : 's'}
                </Button>
              </Popconfirm>
            </div>

            {manifest.selected_total === 0 && (
              <p className='text-gray-500 text-xs mb-2'>
                Nothing to enqueue — every subject in scope is already processed, already queued or
                has no follower instance. Provision instances below if the subjects are new.
              </p>
            )}

            {!!manifest.selected?.length && (
              <>
                <div className='text-xs text-gray-500 mb-1'>Will be enqueued</div>
                <Table
                  size='small'
                  rowKey='subject_id'
                  columns={candidateColumns as any}
                  dataSource={manifest.selected}
                  pagination={false}
                  className='mb-3'
                />
              </>
            )}

            {!!manifest.skipped.length && (
              <>
                <div className='text-xs text-gray-500 mb-1'>
                  Skipped
                  {manifest.skipped_truncated && ' (first 25)'}
                </div>
                <Table
                  size='small'
                  rowKey='subject_id'
                  columns={candidateColumns as any}
                  dataSource={manifest.skipped}
                  pagination={manifest.skipped.length > 10 ? { pageSize: 10 } : false}
                />
              </>
            )}
          </div>
        )}
        </>
        )}
      </FormSection>

      {/* ── Company short descriptions ─────────────────────────────────── */}
      <CompanyShortDescriptionsSection />

      {/* ── Runs ───────────────────────────────────────────────────────── */}
      <FormSection
        title='Runs'
        titleColor='purple'
        loading={runsLoading}
        className='shadow-sm border border-gray-200'
        headerActions={
          <div className='flex items-center gap-2 flex-wrap justify-end'>
            <Button size='small' icon={<ReloadOutlined />} onClick={loadRuns} />
          </div>
        }
      >
        {/* Filters sit in a body row, not headerActions: five controls do not
            fit the header slot, which does not shrink. */}
        <div className='flex items-center gap-2 flex-wrap mb-2'>
          <Select
            size='small'
            className='w-44'
            allowClear
            placeholder='All agents'
            value={runType}
            onChange={(value) => setRunType(value ?? null)}
            options={typeOptions}
          />
          <Select
            size='small'
            className='w-32'
            allowClear
            placeholder='All statuses'
            value={runStatus}
            onChange={(value) => setRunStatus(value ?? null)}
            options={[
              'queued',
              'dispatched',
              'processing',
              'done',
              'skipped',
              'error',
              'unconfigured'
            ].map((s) => ({ value: s, label: s }))}
          />
          <Select
            size='small'
            className='w-32'
            allowClear
            placeholder='All triggers'
            value={runTrigger}
            onChange={(value) => setRunTrigger(value ?? null)}
            options={['interaction', 'schedule', 'manual', 'backfill', 'child_output'].map((t) => ({
              value: t,
              label: t
            }))}
          />
          <DatePicker.RangePicker
            size='small'
            value={runRange as any}
            onChange={(value) => setRunRange(value as [Dayjs | null, Dayjs | null] | null)}
            format='MM/DD/YYYY'
          />
        </div>
        {/* A fixed scroll.x, not 'max-content': these tables carry a free-text
            column (summary / subject), and max-content lets it expand to its
            full untruncated width, which defeats the ellipsis and makes the
            scroll region thousands of pixels wide. */}
        <div ref={runsTableRef}>
          <Table
            size='small'
            rowKey='agent_runs__id'
            columns={runColumns as any}
            dataSource={runs}
            scroll={{ x: 1180, y: TABLE_BODY_HEIGHT }}
            pagination={false}
            locale={{ emptyText: runsLoading ? ' ' : 'No runs match these filters' }}
          />
        </div>
        <div className='h-8 flex items-center text-xs text-gray-400'>
          {runsLoadingMore ? 'Loading more runs…' : windowLabel(runs.length, runsCount, 'run')}
        </div>
      </FormSection>

      {/* ── Instances ──────────────────────────────────────────────────── */}
      <FormSection
        title='Followers'
        titleColor='orange'
        loading={instLoading}
        className='shadow-sm border border-gray-200'
        headerActions={
          <div className='flex items-center gap-2 flex-wrap justify-end'>
            <Button size='small' icon={<ReloadOutlined />} onClick={loadInstances} />
          </div>
        }
      >
        <p className='text-gray-500 text-xs mt-0 mb-2'>
          Every provisioned follower. “Processed to” is the watermark — a subject that has never
          been processed is what Backfill above picks up. Deepen reads the history a processed
          follower never saw: its first backfill kept only the newest interactions inside the
          type’s lookback (Internal Agents → Backfill depth), and everything before that floor is
          otherwise unreachable. Pausing a follower stops its runs without losing its memory or
          signals.
        </p>
        {/* Filters (the Runs row's chrome) and the column sort arrows both go
            to the server: the table is a 100-row window of the fleet, so
            nothing is narrowed or ordered on the client. */}
        <div className='flex items-center gap-2 flex-wrap mb-2'>
          <Select
            size='small'
            className='w-44'
            allowClear
            placeholder='All agents'
            value={instType}
            onChange={(value) => setInstType(value ?? null)}
            options={typeOptions}
          />
          <Select
            size='small'
            className='w-32'
            allowClear
            placeholder='All statuses'
            value={instStatus}
            onChange={(value) => setInstStatus(value ?? null)}
            options={['active', 'paused'].map((s) => ({ value: s, label: s }))}
          />
          <Input
            size='small'
            className='w-56'
            allowClear
            placeholder='Subject contains…'
            value={instSubject}
            onChange={(e) => setInstSubject(e.target.value)}
          />
          <Tooltip title='Followers whose watermark is before this date, plus those never processed'>
            <DatePicker
              size='small'
              className='w-40'
              placeholder='Processed before'
              value={instProcessedBefore}
              onChange={(value) => setInstProcessedBefore(value)}
              format='MM/DD/YYYY'
            />
          </Tooltip>
          <div className='ml-auto'>
            <Popconfirm
              title={`Provision ${
                typeOptions.find((t) => t.value === instType)?.label ?? 'follower'
              } instances?`}
              description='Creates an instance for every subject matching that agent type’s auto-provision rule. Each new instance arms the inbound-mail trigger, so those subjects start enqueueing runs of their own accord.'
              okText='Provision'
              onConfirm={handleProvision}
              disabled={!instType}
            >
              <Tooltip title={instType ? undefined : 'Choose an agent type first'}>
                <Button size='small' loading={busy === 'provision'} disabled={!instType}>
                  Provision instances
                </Button>
              </Tooltip>
            </Popconfirm>
          </div>
        </div>
        <div ref={instTableRef}>
          <Table
            size='small'
            rowKey='agent_instances__id'
            columns={instanceColumns as any}
            dataSource={instances}
            scroll={{ x: 1120, y: TABLE_BODY_HEIGHT }}
            pagination={false}
            onChange={handleInstTableChange}
            locale={{ emptyText: instLoading ? ' ' : 'No followers match these filters' }}
          />
        </div>
        <div className='h-8 flex items-center text-xs text-gray-400'>
          {instLoadingMore ? 'Loading more followers…' : windowLabel(instances.length, instCount, 'follower')}
        </div>
      </FormSection>

      {/* ── Deepen (design §7.4.2) ─────────────────────────────────────── */}
      <Modal
        open={deepenFor != null}
        title={`Deepen backfill — ${deepenFor?.subject_name ?? 'follower'}`}
        onCancel={() => setDeepenFor(null)}
        okText='Queue deepen'
        okButtonProps={{
          disabled: !deepenPreview?.eligible || deepenBusy === 'preview',
          loading: deepenBusy === 'enqueue'
        }}
        onOk={handleDeepen}
        width={640}
      >
        <p className='text-gray-500 text-xs mt-0 mb-3'>
          The first backfill read only the newest interactions inside the type’s lookback, so this
          follower’s history starts at “Reads from”. Deepen queues the interactions before that
          point, down to the date you choose (empty = all of them), as backfill runs that never
          move the watermark, and closes with a review that rebuilds the brief from the whole
          history. It only enqueues — the queue paces the runs against the daily caps, so a long
          chain finishes over several days.
        </p>
        <label className='flex flex-col gap-1 mb-3'>
          <span className='text-xs text-gray-500'>Read back to</span>
          <DatePicker
            size='small'
            allowClear
            placeholder='All earlier history'
            value={deepenFloor}
            onChange={(value) => setDeepenFloor(value)}
            disabledDate={(d) =>
              deepenPreview?.ceiling ? !d.isBefore(dayjs(deepenPreview.ceiling), 'day') : false
            }
            className='w-48'
          />
        </label>
        {deepenPreview ? (
          <div className='flex gap-6 flex-wrap items-center bg-white border border-gray-100 rounded-lg px-4 py-2.5'>
            {tile(
              'Reads from',
              fmtDate(deepenPreview.ceiling),
              'Where this follower’s read history starts: its first backfill window’s floor, or the watermark when it has never read a window.'
            )}
            {tile('Interactions', deepenPreview.interactions_in_range ?? 0, 'Between the chosen date and “Reads from”.')}
            {tile(
              'Runs (est.)',
              deepenPreview.windows_estimate ?? 0,
              `${deepenPreview.window_limit ?? 40} interactions per run. A chain longer than the type’s daily run cap defers to the following days.`
            )}
            {tile('Earliest', fmtDate(deepenPreview.earliest_interaction_at), 'The follower’s oldest interaction on record.')}
            <div className='flex-1' />
            <Tag color={REASON_TAG[deepenPreview.reason] ?? 'gold'}>{reasonLabel(deepenPreview.reason)}</Tag>
          </div>
        ) : (
          <div className='h-8 flex items-center text-xs text-gray-400'>
            {deepenBusy === 'preview' ? 'Previewing…' : ' '}
          </div>
        )}
      </Modal>
    </div>
  )
}
